<script setup generic="T extends RowData" lang="ts">
import type {
  ColumnResizeDirection,
  ColumnResizeMode,
  ColumnSizingInfoState,
  Header,
  RowData,
} from '@tanstack/vue-table'
import { computed } from '#imports'

const { header } = defineProps<{
  header: Header<T, unknown>
  columnSizingInfo: ColumnSizingInfoState
  columnResizeMode?: ColumnResizeMode
  columnResizeDirection?: ColumnResizeDirection
}>()

const align = computed<'end' | 'start'>(() =>
  header.column.columnDef.meta?.align === 'right' ? 'end' : 'start',
)

const onSort = (event: Event) => {
  const handler = header.column.getToggleSortingHandler()
  if (handler) handler(event)
}

const onResize = (event: Event) => {
  const handler = header.getResizeHandler()
  if (handler) handler(event)
}
</script>

<template>
  <div
    class="flex"
    :style="{
      width: `${header.column.getSize()}px`,
    }"
  >
    <!-- TODO: see if we can omit having to define height -->
    <button
      class="subtitle-2 flex h-[36px] flex-1 items-center truncate whitespace-nowrap px-4 py-2 text-neutral-800 outline-none lg:px-6"
      :class="{
        'flex-row-reverse': align === 'end',
        'pr-0!': header.column.getCanResize(),
        'cursor-default': !header.column.getCanSort(),
      }"
      aria-label="table sort"
      type="button"
      @click="onSort"
    >
      <slot />

      <div v-if="header.column.getCanSort()" class="px-2">
        <i
          class="i-ph-arrow-up-bold block h-3 w-3 transition"
          :class="{
            'rotate-180': header.column.getIsSorted() === 'desc',
            'rotate-90 opacity-0': !header.column.getIsSorted(),
          }"
        />
      </div>
    </button>
    <button
      v-if="header.column.getCanResize()"
      class="transform cursor-ew-resize rounded-full text-neutral-800 outline-none"
      :class="{
        'opacity-30': !header.column.getIsResizing(),
        'bg-surface-bg scale-120 border shadow': header.column.getIsResizing(),
      }"
      :style="{
        transform:
          columnResizeMode === 'onEnd' && header.column.getIsResizing()
            ? `translateX(${
                (columnResizeDirection === 'rtl' ? -1 : 1) *
                (columnSizingInfo.deltaOffset ?? 0)
              }px)`
            : '',
      }"
      type="button"
      @mousedown="onResize"
      @touchstart="onResize"
    >
      <i class="i-ph-line-vertical-bold block h-4 w-4" />
    </button>
  </div>
</template>
